/* rc-reviews-io: wrapper around the REVIEWS.io Polaris widget (sections/product-reviews.liquid).
   The app renders 220×220 review thumbnails, but the cards show photos at ~257×354.
   REVIEWS.io's resize service never crops: it letterboxes the photo with white bars
   to the requested box. So each photo is fetched at the lightbox size (900×900, CORS
   enabled), the white bars are trimmed on a canvas and the result is shown with
   object-fit: cover. Re-runs on every widget re-render (pagination, filters, sort). */

const PROBE_SIZE = 900; // same size the REVIEWS.io lightbox requests
const WHITE = 245; // channel value treated as letterbox padding

const trimmedPhotos = new Map(); // original photo URL -> Promise<blob URL>

function photoSource(src) {
  if (!src || src.indexOf('/resize/create') === -1) return null;
  try {
    return new URL(src, window.location.href).searchParams.get('src');
  } catch (e) {
    return null;
  }
}

function isPadding(data, i) {
  return data[i] >= WHITE && data[i + 1] >= WHITE && data[i + 2] >= WHITE;
}

// Bounding box of the non-white content in a letterboxed image.
function contentBox(ctx, width, height) {
  const data = ctx.getImageData(0, 0, width, height).data;
  const rowHasContent = (y) => {
    for (let x = 0; x < width; x += 2) if (!isPadding(data, (y * width + x) * 4)) return true;
    return false;
  };
  const colHasContent = (x, top, bottom) => {
    for (let y = top; y <= bottom; y += 2) if (!isPadding(data, (y * width + x) * 4)) return true;
    return false;
  };

  let top = 0;
  let bottom = height - 1;
  let left = 0;
  let right = width - 1;
  while (top < bottom && !rowHasContent(top)) top++;
  while (bottom > top && !rowHasContent(bottom)) bottom--;
  while (left < right && !colHasContent(left, top, bottom)) left++;
  while (right > left && !colHasContent(right, top, bottom)) right--;

  return { x: left, y: top, width: right - left + 1, height: bottom - top + 1 };
}

function trimPhoto(source) {
  if (trimmedPhotos.has(source)) return trimmedPhotos.get(source);

  const promise = new Promise((resolve, reject) => {
    const probe = new Image();
    probe.crossOrigin = 'anonymous';
    probe.onerror = reject;
    probe.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = probe.naturalWidth;
      canvas.height = probe.naturalHeight;
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      ctx.drawImage(probe, 0, 0);

      const box = contentBox(ctx, canvas.width, canvas.height);
      const out = document.createElement('canvas');
      out.width = box.width;
      out.height = box.height;
      out.getContext('2d').drawImage(canvas, box.x, box.y, box.width, box.height, 0, 0, box.width, box.height);
      out.toBlob((blob) => (blob ? resolve(URL.createObjectURL(blob)) : reject(new Error('toBlob failed'))), 'image/jpeg', 0.9);
    };
    probe.src = 'https://media.reviews.co.uk/resize/create?format=jpg'
      + '&height=' + PROBE_SIZE + '&width=' + PROBE_SIZE
      + '&src=' + encodeURIComponent(source);
  });

  promise.catch(() => trimmedPhotos.delete(source));
  trimmedPhotos.set(source, promise);
  return promise;
}

class RcReviewsIo extends HTMLElement {

  connectedCallback() {
    this.updatePhotos();
    this.observer = new MutationObserver(() => this.updatePhotos());
    this.observer.observe(this, { childList: true, subtree: true, attributes: true, attributeFilter: ['src'] });
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }

  updatePhotos() {
    this.querySelectorAll('.R-ContentList .R-PhotoVideoThumbnail__image').forEach((img) => {
      const source = photoSource(img.getAttribute('src'));
      if (!source || img.dataset.rcSource === source) return;
      img.dataset.rcSource = source;
      trimPhoto(source).then((url) => {
        // Vue may have reused this <img> for another review in the meantime.
        if (img.dataset.rcSource === source) img.setAttribute('src', url);
      }).catch(() => { /* keep the app's thumbnail */ });
    });
  }

}

if (!customElements.get('rc-reviews-io')) customElements.define('rc-reviews-io', RcReviewsIo);


/* rc-reviews-rating: star rating + review count from REVIEWS.io, used on the PDP title row
   (sections/main-product.liquid) and by snippets/product-rating.liquid (product cards,
   Complete the look, quick cart). Hidden until the product has reviews.

   - data-source="timeline" (PDP): the same /timeline/data request and SKU list the reviews
     widget below uses, so the two counts always match.
   - default: /product/rating-batch, the endpoint REVIEWS.io's own rating snippet uses. Every
     rating on the page asks within the same tick and is answered by one request. */
const STAR_PATH = 'M7.94238 4.74251L8.04102 4.97396L8.29102 4.99544L11.9805 5.30794L9.17773 7.73665L8.98828 7.90072L9.04492 8.14583L9.88672 11.7533L6.71484 9.83919L6.5 9.70931L6.28516 9.83919L3.1123 11.7533L3.95508 8.14583L4.01172 7.90072L3.82227 7.73665L1.01855 5.30794L4.70898 4.99544L4.95898 4.97396L5.05762 4.74251L6.5 1.33822L7.94238 4.74251Z';
const RIO_API = 'https://api.reviews.io';

const ratingCache = new Map(); // `${store}|${sku list}` -> Promise<{ count, average }>
const batchQueue = new Map(); // store -> [{ skus, resolve }]
let batchTimer = null;

function splitSkus(sku) {
  return sku.split(';').map((s) => s.trim()).filter(Boolean);
}

// Sums the rows that belong to one product (matched on sku or its lookup list), like the snippet does.
function summarise(rows, skus) {
  const wanted = skus.map((s) => s.toLowerCase());
  const seen = new Set();
  let count = 0;
  let total = 0;
  rows.forEach((row) => {
    if (!row || row.sku == null || seen.has(row.sku)) return;
    const rowSku = String(row.sku).trim().toLowerCase();
    const lookup = row.lookup ? String(row.lookup).toLowerCase().split(';') : [];
    if (!wanted.some((s) => s === rowSku || lookup.includes(s))) return;
    seen.add(row.sku);
    const n = parseFloat(row.num_ratings) || 0;
    count += n;
    total += (parseFloat(row.average_rating) || 0) * n;
  });
  return { count, average: count ? total / count : 0 };
}

async function flushBatch() {
  batchTimer = null;
  const queued = Array.from(batchQueue.entries());
  batchQueue.clear();

  await Promise.all(queued.map(async ([store, waiters]) => {
    const allSkus = Array.from(new Set(waiters.flatMap((w) => w.skus)));
    const body = `sku=${encodeURIComponent(allSkus.join(';'))}&store=${encodeURIComponent(store)}`;
    let rows = [];
    try {
      // Long lists go as a form POST, as the REVIEWS.io snippet does.
      const response = body.length > 8000
        ? await fetch(`${RIO_API}/product/rating-batch`, { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body })
        : await fetch(`${RIO_API}/product/rating-batch?${body}`);
      if (response.ok) {
        const data = await response.json();
        if (Array.isArray(data)) rows = data;
      }
    } catch (e) {
      // Treated as "no reviews" — the rows stay hidden.
    }
    waiters.forEach((w) => w.resolve(summarise(rows, w.skus)));
  }));
}

function batchRating(store, sku) {
  const key = `${store}|${sku}`;
  if (!ratingCache.has(key)) {
    ratingCache.set(key, new Promise((resolve) => {
      if (!batchQueue.has(store)) batchQueue.set(store, []);
      batchQueue.get(store).push({ skus: splitSkus(sku), resolve });
      if (!batchTimer) batchTimer = window.setTimeout(flushBatch, 50);
    }));
  }
  return ratingCache.get(key);
}

async function timelineRating(store, sku) {
  const params = new URLSearchParams({ type: 'product_review', store, page: '1', per_page: '1', sku, lang: 'en' });
  try {
    const response = await fetch(`${RIO_API}/timeline/data?${params.toString()}`);
    if (!response.ok) return { count: 0, average: 0 };
    const stats = (await response.json()).stats || {};
    return { count: parseInt(stats.review_count, 10) || 0, average: parseFloat(stats.average_rating) || 0 };
  } catch (e) {
    return { count: 0, average: 0 };
  }
}

function starPercents(average) {
  return [1, 2, 3, 4, 5].map((i) => Math.round(Math.min(1, Math.max(0, average - (i - 1))) * 100));
}

class RcReviewsRating extends HTMLElement {

  async connectedCallback() {
    const { store, sku, source } = this.dataset;
    if (!store || !sku || this.loaded) return;
    this.loaded = true;
    const { count, average } = source === 'timeline' ? await timelineRating(store, sku) : await batchRating(store, sku);
    if (count > 0) this.render(average, count);
  }

  render(average, count) {
    const shown = this.dataset.fixedRating ? parseFloat(this.dataset.fixedRating) : average;
    const stars = this.querySelector('[data-rating-stars]');
    const label = this.querySelector('[data-rating-count]');
    if (stars) {
      const stops = stars.querySelectorAll('[data-star-stop]');
      const percents = starPercents(shown);
      if (stops.length) {
        // Server-rendered stars (snippets/product-rating.liquid): two gradient stops per star.
        stops.forEach((stop, i) => stop.setAttribute('offset', `${percents[Math.floor(i / 2)]}%`));
      } else {
        const uid = this.dataset.uid || 'rio-star';
        stars.innerHTML = percents.map((pct, i) => `<svg viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">`
          + `<defs><linearGradient id="${uid}-${i + 1}" x1="0" y1="0" x2="13" y2="0" gradientUnits="userSpaceOnUse">`
          + `<stop offset="${pct}%" stop-color="#111111"/><stop offset="${pct}%" stop-color="#ffffff"/></linearGradient></defs>`
          + `<path d="${STAR_PATH}" fill="url(#${uid}-${i + 1})" stroke="#111111" stroke-width="0.833333"/></svg>`).join('');
      }
      stars.setAttribute('aria-label', `${Math.round(shown * 10) / 10} out of 5 stars`);
    }
    if (label) {
      const text = `${count} ${count === 1 ? 'review' : 'reviews'}`;
      label.textContent = this.dataset.countFormat === 'brackets' ? `(${text})` : text;
    }
    this.hidden = false;
  }

}

if (!customElements.get('rc-reviews-rating')) customElements.define('rc-reviews-rating', RcReviewsRating);
